import { Head, Link } from "@inertiajs/react";
import {
  Box,
  Grid,
  Stack,
  Group,
  Button,
  Image,
} from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import PageShell from "../Components/Layout/PageShell";
import {
  Heading1,
  Heading2,
  DimmedText,
  Lead,
} from "../Components/UI/Typography";
import { ProjectCard } from "../Components/ProjectCard";
import { PillarCard } from "../Components/PillarCard";
import { PillarChip } from "../Components/PillarChip";
import { TestimonialWall } from "../Components/TestimonialWall";
import { getPublishedProjects } from "../data/projects";
import { getFeaturedTestimonials } from "../data/testimonials";
import { getPillars } from "../data/pillars";

// Adapter: convert camelCase data/projects.ts shape to snake_case for ProjectCard
function toCardFormat(p: ReturnType<typeof getPublishedProjects>[number]) {
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    description: p.description,
    client_name: p.clientName,
    project_url: p.projectUrl,
    project_type: p.projectType,
    featured_image_url: p.featuredImageUrl,
    skills: p.skills,
    tools: p.tools,
    has_carousel: false,
  };
}

export default function Home() {
  const heroPhotoUrl =
    import.meta.env.VITE_HERO_PHOTO_URL || "/images/charles-hero.png";

  const featuredProjects = getPublishedProjects().slice(0, 3).map(toCardFormat);
  const featuredTestimonials = getFeaturedTestimonials();
  const pillars = getPillars();

  return (
    <>
      <Head>
        <title>Ogarocious — Creative Technologist</title>
        <meta
          name="description"
          content="Ogarocious — full-stack developer, digital media producer, educator, and community builder. Content, code, and systems for impact."
        />
        <meta property="og:title" content="Ogarocious — Creative Technologist" />
        <meta
          property="og:description"
          content="Solo founder and builder, Urban Kiz educator, photographer, and community organizer. Content, code, and systems for impact."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={import.meta.env.VITE_SITE_URL || ""} />
        <meta
          property="og:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Ogarocious — Creative Technologist" />
        <meta
          name="twitter:description"
          content="Content, code, and systems for impact."
        />
        <meta
          name="twitter:image"
          content={import.meta.env.VITE_OG_IMAGE_URL || ""}
        />
      </Head>

      <PageShell variant="hero">
          {/* Section-to-section rhythm via flex + clamp() — see
              feedback_mantine_responsive_gap.md. Mantine 8's Stack gap
              silently no-ops on responsive objects (renders at 16px
              default). Was casting through TS and rendering broken. */}
          <Box
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "clamp(56px, 6vw, 96px)",
            }}
          >
            {/* ── Hero ── */}
            <Grid gutter="xl" align="center">
              <Grid.Col span={{ base: 12, md: 7 }}>
                <Stack gap="md">
                  <Heading1 variant="gradient">Ogarocious</Heading1>

                  <Lead>
                    Tech, dance, media, and the writing that connects them.
                  </Lead>

                  {/* Desktop-only elaboration; on mobile the pillar cards
                      below carry this weight without the redundancy. */}
                  <Box visibleFrom="md">
                    <DimmedText size="lg" style={{ maxWidth: "clamp(280px, 90vw, 520px)", lineHeight: 1.7 }}>
                      Solo founder and builder. I ship software for my own
                      products and for organizations that need it, teach
                      Urban Kiz across cities and continents, shoot the
                      events that shape Austin&apos;s creative scene, and
                      write about the ideas connecting all of it.
                    </DimmedText>
                  </Box>

                  {/* Desktop-only hero CTAs. On mobile the pillar chips
                      below act as the primary discovery affordance, and
                      a single "Get In Touch" button follows. */}
                  <Group mt="md" gap="md" visibleFrom="sm">
                    <Button
                      variant="gradient"
                      size="lg"
                      component={Link}
                      href="/tech"
                    >
                      See Projects
                    </Button>
                    <Button
                      variant="outline"
                      size="lg"
                      component={Link}
                      href="/contact"
                    >
                      Get In Touch
                    </Button>
                  </Group>

                  {/* Mobile-only: 2×2 pillar chips as the above-the-fold
                      discovery mechanism, followed by a single secondary
                      CTA. Replaces both the hero CTA row and the full
                      Four Disciplines section on mobile. */}
                  <Box hiddenFrom="sm" mt="md">
                    <Stack gap="md">
                      <Grid gutter="sm">
                        {pillars.map((pillar) => (
                          <Grid.Col span={6} key={pillar.heading}>
                            <PillarChip pillar={pillar} />
                          </Grid.Col>
                        ))}
                      </Grid>
                      <Button
                        variant="gradient"
                        size="md"
                        component={Link}
                        href="/contact"
                        fullWidth
                      >
                        Get In Touch
                      </Button>
                    </Stack>
                  </Box>
                </Stack>
              </Grid.Col>

              <Grid.Col span={{ base: 12, md: 5 }} visibleFrom="md">
                <Image
                  src={heroPhotoUrl}
                  alt="Ogarocious"
                  fit="contain"
                  radius="md"
                  style={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Grid.Col>
            </Grid>

            {/* ── Four Pillars — desktop+ only.
                 On mobile the 2×2 PillarChip grid inside the hero
                 replaces this section (chips are the discovery
                 mechanism; the full cards would duplicate them). ── */}
            <Box visibleFrom="sm">
              <Stack gap="md" align="center" mb={56}>
                <Heading2 variant="shadow" style={{ textAlign: "center" }}>
                  Four Disciplines
                </Heading2>
                <DimmedText
                  size="lg"
                  style={{
                    textAlign: "center",
                    maxWidth: "clamp(280px, 90vw, 520px)",
                  }}
                >
                  One person, four practices — each connected by the same
                  underlying logic: design a system, earn trust, keep it
                  running.
                </DimmedText>
              </Stack>

              <Grid gutter="md">
                {pillars.map((pillar) => (
                  <Grid.Col
                    key={pillar.heading}
                    span={{ base: 12, sm: 6, lg: 3 }}
                  >
                    <PillarCard pillar={pillar} />
                  </Grid.Col>
                ))}
              </Grid>
            </Box>

            {/* ── Mobile-only "who" band (photo lives here on mobile; on
                 desktop it renders inside the hero Grid above). ── */}
            <Box hiddenFrom="md">
              <Stack gap="sm" align="center">
                <Image
                  src={heroPhotoUrl}
                  alt="Ogarocious"
                  fit="contain"
                  radius="md"
                  style={{
                    width: "100%",
                    maxWidth: 360,
                    height: "auto",
                    display: "block",
                  }}
                />
                <DimmedText size="sm" style={{ textAlign: "center" }}>
                  That&apos;s me — Charles Ogar.
                </DimmedText>
              </Stack>
            </Box>

            {/* ── Featured Projects ── */}
            {featuredProjects.length > 0 && (
              <Box>
                <Group justify="space-between" align="center" mb="lg">
                  <Heading2 variant="shadow">Featured Work</Heading2>
                  <Button
                    variant="subtle"
                    color="teal"
                    size="sm"
                    component={Link}
                    href="/tech"
                    rightSection={<IconArrowRight size={16} />}
                  >
                    See All Tech Projects
                  </Button>
                </Group>

                <Grid>
                  {featuredProjects.map((project) => (
                    <Grid.Col
                      key={project.id}
                      span={{ base: 12, sm: 6, lg: 4 }}
                    >
                      <ProjectCard project={project} />
                    </Grid.Col>
                  ))}
                </Grid>
              </Box>
            )}

            {/* ── Wall of Love ── */}
            {featuredTestimonials.length > 0 && (
              <Box>
                <Group justify="space-between" align="center" mb="lg">
                  <Heading2 variant="shadow">Kind Words</Heading2>
                  <Button
                    variant="subtle"
                    color="teal"
                    size="sm"
                    component={Link}
                    href="/contact"
                    rightSection={<IconArrowRight size={16} />}
                  >
                    Work With Me
                  </Button>
                </Group>

                <TestimonialWall
                  testimonials={featuredTestimonials}
                  featuredOnly={true}
                  showFilters={false}
                />
              </Box>
            )}
        </Box>
      </PageShell>
    </>
  );
}
